import { createFileRoute } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { NotificationInbox } from '../../features/notifications/components/notification-inbox';

const NotificationSearch = z.object({
  /** Bộ lọc nằm ở URL để chia sẻ được (docs/frontend-guide.md §2). */
  unreadOnly: z.boolean().optional().catch(undefined),
});

/**
 * Hộp thư thông báo, dạng **trang** — APM-NOTI-c.
 *
 * Không đòi quyền gì: đây là dữ liệu của chính người đang đăng nhập, và API tự
 * lấy người nhận từ token nên không có phạm vi nào để chọn nhầm.
 *
 * ## Vì sao trang này còn sống dù chuông đã mở lớp phủ — APM-ORG-h12
 *
 * Chuông là đường **thường ngày**, nhưng nó không phải đường duy nhất: link cũ,
 * dấu trang, và `?unreadOnly=true` gửi cho nhau đều dẫn về đây. Bỏ route thì
 * những đường ấy thành 404 mà không ai biết trước.
 *
 * Nội dung là cùng một component với lớp phủ (`NotificationInbox`) — hai bản
 * chép tay của cùng một hộp thư là hai chỗ phải sửa mỗi lần thêm một loại
 * thông báo.
 */
export const Route = createFileRoute('/_app/notifications')({
  validateSearch: NotificationSearch,
  component: NotificationsPage,
});

function NotificationsPage(): ReactNode {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <div className="space-y-4">
      <h1 className="flex items-center gap-2 text-xl font-semibold">
        <Bell className="h-5 w-5" aria-hidden />
        {t('notifications.title')}
      </h1>

      <NotificationInbox
        unreadOnly={search.unreadOnly === true}
        onUnreadOnlyChange={(next) =>
          void navigate({ search: next ? { unreadOnly: true } : {}, replace: true })
        }
      />
    </div>
  );
}
